import { useRouter } from 'next/router';
import React, { useCallback, useMemo } from 'react';
import { useAppState } from '../../client/providers/AppProvider';
import { Espin_Paid_Product_Enum, Espin_Role_Enum } from '../../generated/spin-graphql';
import { SpinAppModals, useModals } from '../common/SpinModalProvider';
import { SPINIcon } from '../svg/SPINIcon';

type Props = {
    className?: string;
    monthly: boolean;
    label: string;
    value: string;
    valueCents?: string;
    ctaLabel?: string;
    currentPlan: boolean;
    freeTrialOffer?: boolean;
    showFreeTrial?: boolean;
    showReferralOptionModal?: boolean;
    hasInterval?: boolean;
    cardHighlightText?: string;
    subscriptionTier?: Espin_Paid_Product_Enum;
    contactUsEnabled?: boolean;
};

export const PricingOption: React.FC<Props> = ({
    className,
    monthly,
    label,
    value,
    valueCents,
    ctaLabel,
    currentPlan,
    freeTrialOffer = false,
    showFreeTrial = false,
    showReferralOptionModal = false,
    hasInterval = true,
    cardHighlightText = '',
    subscriptionTier,
    contactUsEnabled,
}) => {
    const { openModal } = useModals();
    const { user } = useAppState();
    const router = useRouter();
    const callback = useCallback(() => {
        if (!!!user) {
            return openModal(SpinAppModals.SIGNUP_SELECT_ACCT_TYPE);
        }
        return router.push(`/dashboard/subscriptions`);
    }, [openModal, router, user]);
    const onSubPage = router.pathname === '/dashboard/subscriptions';

    return (
        <div className={`pricing-option-container${cardHighlightText != '' ? '' : ' margin-top'}`}>
            {cardHighlightText != '' && <div className="card-highlight">{cardHighlightText}</div>}
            <div className={`pricing-option${className ? ` ${className}` : ''}${currentPlan ? ' current' : ''}`}>
                <SPINIcon className="pricing-background-icon" />
                <h3 className="pricing-label">{label}</h3>
                <h2 className="pricing-value">
                    {value}
                    {valueCents && <span className="value-cents">{valueCents}</span>}
                </h2>
                <p className={`pricing-interval`}>{hasInterval && <>{monthly ? 'PER MONTH' : 'PER YEAR'}</>}</p>

                {onSubPage && (
                    <InDashboardPricingButton
                        subscriptionTier={subscriptionTier}
                        monthly={monthly}
                        ctaLabel={ctaLabel}
                        enableCTA={!currentPlan}
                        contactUsEnabled={contactUsEnabled}
                        freeTrialOffer={freeTrialOffer}
                        showFreeTrial={showFreeTrial}
                        showReferralOptionModal={showReferralOptionModal}
                    />
                )}
                {!onSubPage && (
                    <>
                        {freeTrialOffer ? (
                            <div className="free-trial-container">
                                <span className="free-trial-descriptor">Free 7 Day Trial</span>
                                <button
                                    onClick={() => callback()}
                                    className={`button large-button${ctaLabel ? '' : ' hidden'}`}
                                >
                                    {showFreeTrial ? 'Start Free Trial' : ctaLabel}
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => callback()}
                                className={`button large-button${ctaLabel ? '' : ' hidden'}`}
                            >
                                {ctaLabel}
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

function InDashboardPricingButton({
    ctaLabel,
    monthly,
    enableCTA,
    freeTrialOffer = false,
    showFreeTrial = false,
    showReferralOptionModal = false,
    subscriptionTier = Espin_Paid_Product_Enum.CollegePremiumYearly,
    contactUsEnabled = false,
}: {
    ctaLabel: string;
    monthly: boolean;
    enableCTA: boolean;
    freeTrialOffer?: boolean;
    showFreeTrial?: boolean;
    showReferralOptionModal?: boolean;
    subscriptionTier?: Espin_Paid_Product_Enum;
    contactUsEnabled?: boolean;
}) {
    const { user } = useAppState();
    const { openModal } = useModals();
    const plan = useMemo(() => {
        if (user.role === Espin_Role_Enum.EspinCollegeUser) {
            return subscriptionTier ?? Espin_Paid_Product_Enum.TierOneCollegeYearlyPlan;
        } else {
            if (monthly) {
                return Espin_Paid_Product_Enum.PlayerPremiumMonthly;
            }
            return Espin_Paid_Product_Enum.PlayerPremiumYearly;
        }
    }, [monthly, user.role, subscriptionTier]);

    if (contactUsEnabled) {
        return (
            <button
                onClick={() => (location.href = 'mailto:info@staypluggedin.com')}
                className={`button large-button${ctaLabel ? '' : ' hidden'}`}
            >
                {ctaLabel}
            </button>
        );
    }

    if (!enableCTA) {
        return <button className={`button large-button${ctaLabel ? '' : ' hidden'}`}>{ctaLabel}</button>;
    }

    return (
        <>
            {showReferralOptionModal && (
                <>
                    {freeTrialOffer && showFreeTrial && user.role === Espin_Role_Enum.EspinPlayerUser ? (
                        <div className="free-trial-container">
                            <span className="free-trial-descriptor">Free 7 Day Trial</span>
                            <button
                                onClick={() => {
                                    openModal(SpinAppModals.SUB_SELECT_REFERRAL_OPTION, {
                                        user,
                                        freeTrial: true,
                                        plan,
                                    });
                                }}
                                className={`button large-button${ctaLabel ? '' : ' hidden'}`}
                            >
                                Start Free Trial
                            </button>
                        </div>
                    ) : (
                        <button
                            onClick={() => {
                                openModal(SpinAppModals.SUB_SELECT_REFERRAL_OPTION, {
                                    user,
                                    freeTrial: false,
                                    plan,
                                });
                            }}
                            className={`button large-button${ctaLabel ? '' : ' hidden'}`}
                        >
                            {ctaLabel}
                        </button>
                    )}
                </>
            )}
            {!showReferralOptionModal && (
                <>
                    {freeTrialOffer && showFreeTrial && user.role === Espin_Role_Enum.EspinPlayerUser ? (
                        <div className="free-trial-container">
                            <span className="free-trial-descriptor">Free 7 Day Trial</span>
                            <form
                                action="/api/payments/checkout-sessions"
                                method="POST"
                                className="journey-pricing-option-form"
                            >
                                <input type="hidden" name="product" value={plan} />
                                <input type="hidden" name="email" value={user?.email} />
                                <input type="hidden" name="userid" value={user?.id} />
                                <input type="hidden" name="role" value={user?.role} />
                                <input type="hidden" name="freeTrial" value={1} />
                                <button className={`button large-button${ctaLabel ? '' : ' hidden'}`}>
                                    Start Free Trial
                                </button>
                            </form>
                        </div>
                    ) : (
                        <form
                            action="/api/payments/checkout-sessions"
                            method="POST"
                            className="journey-pricing-option-form"
                        >
                            <input type="hidden" name="product" value={plan} />
                            <input type="hidden" name="email" value={user?.email} />
                            <input type="hidden" name="userid" value={user?.id} />
                            <input type="hidden" name="role" value={user?.role} />
                            <button className={`button large-button${ctaLabel ? '' : ' hidden'}`}>{ctaLabel}</button>
                        </form>
                    )}
                </>
            )}
        </>
    );
}
